import React from 'react'
import styles from './SelectInput.module.css'
import { ChevronDown, AlertCircle } from 'lucide-react'

/**
 * SelectInput
 *
 * Reusable labeled dropdown. Accepts options as an array of either
 * plain strings or { value, label } objects.
 *
 * Usage:
 *   <SelectInput
 *     label="Redirect type"
 *     value={form.redirectType}
 *     onChange={(v) => updateField('redirectType', v)}
 *     options={[
 *       { value: 301, label: '301 — Permanent' },
 *       { value: 302, label: '302 — Temporary' }
 *     ]}
 *   />
 *
 *   <SelectInput
 *     label="Font family"
 *     value={form.fontFamily}
 *     onChange={(v) => updateField('fontFamily', v)}
 *     options={FONT_OPTIONS}
 *   />
 */
const SelectInput = ({
    label,
    value,
    onChange,
    options = [],
    placeholder,
    error,
    helperText,
    disabled = false,
    required = false,
    name,
    id
}) => {
    const inputId = id || name || label?.replace(/\s+/g, '-').toLowerCase()

    const normalized = options.map((opt) =>
        typeof opt === 'object' && opt !== null ? opt : { value: opt, label: String(opt) }
    )

    return (
        <div className={styles.field}>
            {label && (
                <label className={styles.label} htmlFor={inputId}>
                    {label}
                    {required && <span className={styles.required}>*</span>}
                </label>
            )}

            <div className={styles.selectWrap}>
                <select
                    id={inputId}
                    name={name}
                    className={`${styles.select} ${error ? styles.selectError : ''}`}
                    value={value ?? ''}
                    onChange={(e) => onChange?.(e.target.value)}
                    disabled={disabled}
                    aria-invalid={!!error}
                >
                    {placeholder && (
                        <option value="" disabled>
                            {placeholder}
                        </option>
                    )}
                    {normalized.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </select>
                <ChevronDown size={14} className={styles.chevron} />
            </div>

            {error ? (
                <span className={styles.errorText}>
                    <AlertCircle size={12} />
                    {error}
                </span>
            ) : helperText ? (
                <span className={styles.helperText}>{helperText}</span>
            ) : null}
        </div>
    )
}

export default SelectInput